import { BaseModel } from "@/models/base.model"
import { type IBase } from "@/interfaces/api/base.interface"
import { BaseRepository } from "@/repositories/base-repository"
import { BaseAPIFunctions } from "@/api/base-api-functions"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export class GenericAPIFunctions<
  Model extends BaseModel,
  IData extends IBase,
  Repository extends BaseRepository<IData>
> extends BaseAPIFunctions<Model, IData, Repository> {
  constructor(repository: Repository) {
    super(repository)
  }

  public async getModel(
    id: string,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<IData | null> {
    const response = await this.repository.get(id, requestConfig)
    return response.data || null
  }

  public async getModels(requestConfig?: IRepositoryRequestConfig): Promise<IData[]> {
    if (!requestConfig) {
      requestConfig = {}
    }

    const response = await this.repository.getList(requestConfig)

    return response.data
  }

  public async createModel(data: IData, requestConfig?: IRepositoryRequestConfig): Promise<IData>
  public async createModel(
    data: IData[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<IData[]>
  public async createModel(
    data: IData | IData[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<IData | IData[]> {
    let response
    if (Array.isArray(data)) {
      response = await this.repository.bulkCreate(data, requestConfig) // inferred as IData[]
    } else {
      response = await this.repository.create(data, requestConfig) // inferred as IData
    }
    return response.data
  }

  public async updateModel(data: IData, requestConfig?: IRepositoryRequestConfig): Promise<IData>
  public async updateModel(
    data: IData[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<IData[]>
  public async updateModel(
    data: IData | IData[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<IData | IData[]> {
    if (Array.isArray(data)) {
      // validate all have ids
      for (const d of data) {
        if (!d.id) {
          throw new Error("Model ID are required to bulk update models")
        }
      }

      const response = await this.repository.bulkUpdate(data, requestConfig)
      return response.data
    } else {
      if (!data.id) {
        throw new Error("Model ID is required to update a model")
      }

      const response = await this.repository.update(data.id, data, requestConfig)
      return response.data
    }
  }

  public async deleteModel(model: Model, requestConfig?: IRepositoryRequestConfig): Promise<boolean>
  public async deleteModel(
    model: Model[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<boolean>
  public async deleteModel(
    data: Model | Model[],
    requestConfig?: IRepositoryRequestConfig
  ): Promise<boolean> {
    if (Array.isArray(data)) {
      // Validate all have ids
      for (const d of data) {
        if (!d.id) {
          throw new Error("Model IDs are required to bulk delete models")
        }
      }

      const ids = data.map((d) => d.id!)
      const response = await this.repository.bulkDelete(ids, requestConfig)

      // bulkDelete should return a 204 if successful
      return response.status === 204
    } else {
      if (!data.id) {
        throw new Error("Model ID is required to delete a model")
      }

      const response = await this.repository.delete(data.id, requestConfig)
      return response.status === 204
    }
  }
}
